import {
  isWireToothEdited,
  type WireTeethModel,
  type WireTooth,
} from "./wireframeTeethShape";
import type { Point2D } from "./types";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load the after image"));
    img.src = src;
  });
}

function centroid(pts: Point2D[]): Point2D {
  let x = 0;
  let y = 0;
  for (const p of pts) {
    x += p.x;
    y += p.y;
  }
  return { x: x / pts.length, y: y / pts.length };
}

function pathContour(ctx: CanvasRenderingContext2D, pts: Point2D[]): void {
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.closePath();
}

function sourceBox(pts: Point2D[], pad: number, width: number, height: number) {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of pts) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  x0 = Math.max(0, Math.floor(x0 - pad));
  y0 = Math.max(0, Math.floor(y0 - pad));
  x1 = Math.min(width, Math.ceil(x1 + pad));
  y1 = Math.min(height, Math.ceil(y1 + pad));
  return { x0, y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}

function sampleCover(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  contour: Point2D[]
): string {
  let minY = Infinity;
  for (const p of contour) minY = Math.min(minY, p.y);
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (const p of contour) {
    if (p.y > minY + 8) continue;
    const x = Math.max(0, Math.min(width - 1, Math.round(p.x)));
    const y = Math.max(0, Math.min(height - 1, Math.round(p.y - 5)));
    const i = (y * width + x) * 4;
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    n++;
  }
  if (!n) return "rgb(150,90,80)";
  return `rgb(${Math.round(r / n)},${Math.round(g / n)},${Math.round(b / n)})`;
}

function drawWarpedTooth(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  tooth: WireTooth,
  width: number,
  height: number
): void {
  const pts = tooth.base;
  if (pts.length < 3) return;
  const c = centroid(pts);
  const sx = tooth.scaleX ?? 1;
  const sy = tooth.scaleY ?? 1;
  const dx = tooth.shiftX ?? 0;
  const dy = tooth.shiftY ?? 0;
  const box = sourceBox(pts, 3, width, height);
  ctx.save();
  ctx.translate(c.x + dx, c.y + dy);
  ctx.scale(sx, sy);
  ctx.translate(-c.x, -c.y);
  pathContour(ctx, pts);
  ctx.clip();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, box.x0, box.y0, box.w, box.h, box.x0, box.y0, box.w, box.h);
  ctx.restore();
}

/**
 * Paint the after photo with live tooth width / height / position applied
 * to the enamel pixels themselves (not a wireframe overlay).
 */
export function paintEditedAfter(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  model: WireTeethModel,
  coverPixels?: Uint8ClampedArray | null
): void {
  const { width, height } = model;
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const edited = model.teeth.filter(isWireToothEdited);
  if (!edited.length) return;

  for (const tooth of edited) {
    if (coverPixels) {
      ctx.fillStyle = sampleCover(coverPixels, width, height, tooth.base);
    } else {
      ctx.fillStyle = "rgb(150,90,80)";
    }
    pathContour(ctx, tooth.base);
    ctx.fill();
  }
  for (const tooth of edited) {
    drawWarpedTooth(ctx, img, tooth, width, height);
  }
}

/** After photo with tooth dimensions applied, no outline strokes. */
export async function compositeEditedAfter(
  afterSrc: string,
  model: WireTeethModel
): Promise<string> {
  const img = await loadImage(afterSrc);
  const canvas = document.createElement("canvas");
  canvas.width = model.width;
  canvas.height = model.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not draw the edited after");
  const probe = document.createElement("canvas");
  probe.width = model.width;
  probe.height = model.height;
  const pctx = probe.getContext("2d", { willReadFrequently: true });
  let cover: Uint8ClampedArray | null = null;
  if (pctx) {
    pctx.drawImage(img, 0, 0, model.width, model.height);
    cover = pctx.getImageData(0, 0, model.width, model.height).data;
  }
  paintEditedAfter(ctx, img, model, cover);
  return canvas.toDataURL("image/png");
}

/** @deprecated outlines-only snapshot; prefer compositeEditedAfter */
export async function compositeOutlinesOnAfter(
  afterSrc: string,
  model: WireTeethModel
): Promise<string> {
  return compositeEditedAfter(afterSrc, model);
}
